import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enTranslation from './locales/en/translation.json';
import frTranslation from './locales/fr/translation.json';
import deTranslation from './locales/de/translation.json';
import esTranslation from './locales/es/translation.json';
import itTranslation from './locales/it/translation.json';
import plTranslation from './locales/pl/translation.json';

const resources = {
  en: { translation: enTranslation },
  fr: { translation: frTranslation },
  de: { translation: deTranslation },
  es: { translation: esTranslation },
  it: { translation: itTranslation },
  pl: { translation: plTranslation },
};

const FALLBACK_LANGUAGE = 'en';
const STORAGE_KEY = 'i18nextLng';
const SUPPORTED_LANGUAGE_CODES = Object.keys(resources);

const normalizeLanguage = (lng?: string | null) => {
  if (!lng) {
    return FALLBACK_LANGUAGE;
  }

  const lowerCased = lng.toLowerCase();

  if (SUPPORTED_LANGUAGE_CODES.includes(lowerCased)) {
    return lowerCased;
  }

  const baseCode = lowerCased.split('-')[0];
  if (SUPPORTED_LANGUAGE_CODES.includes(baseCode)) {
    return baseCode;
  }

  return FALLBACK_LANGUAGE;
};

const persistLanguage = (lng: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (localStorage.getItem(STORAGE_KEY) !== lng) {
      localStorage.setItem(STORAGE_KEY, lng);
    }
  } catch {
    // Silently ignore storage errors (e.g., disabled cookies)
  }
};

const updateDocumentLanguage = (lng: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
};

const applyLanguageSideEffects = (lng: string) => {
  const normalized = normalizeLanguage(lng);

  if (normalized !== lng) {
    i18n.changeLanguage(normalized);
    return;
  }

  persistLanguage(normalized);
  updateDocumentLanguage(normalized);
};

i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    resources,
    fallbackLng: FALLBACK_LANGUAGE,
    debug: import.meta.env.DEV,

    interpolation: {
      escapeValue: false, // React already escapes by default
    },

    // Language detection configuration
    detection: {
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: STORAGE_KEY,
      convertDetectedLanguage: normalizeLanguage,
    },

    // React-specific configuration
    react: {
      useSuspense: false,
    },
  });

// Normalize locale codes (e.g., fr-FR → fr), persist to localStorage and update document language
i18n.on('initialized', () => {
  applyLanguageSideEffects(i18n.language);
});

i18n.on('languageChanged', applyLanguageSideEffects);

export default i18n;
